import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Cookies from 'js-cookie';
import { Mic, Download, Loader2, RefreshCw, Lock, FileSpreadsheet, AlertTriangle, CheckCircle2 } from 'lucide-react';
import api from '@/utils/api';
import { useUser } from '@/contexts/UserContext';
import { toastMessage } from '@/utils/toast-message';
import { ReportNotes } from '@/pages/reports/_Components/InfoTip';

interface Filters {
  from_date: string; to_date: string; agent: string; call_type: string;
  answered_only: boolean; min_duration: number;
}
interface Preview { total: number; agents: string[]; columns: string[]; rows: Record<string, string>[]; }
interface Job {
  job_id: string; status: 'queued' | 'running' | 'done' | 'failed';
  filename: string; filters: Partial<Filters>; created_by: string; created_at: string;
  rows: number | null; error: string | null; finished_at: string | null;
}

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://api.growtheye.com/api';
const MAX_DAYS = 93;
const PREVIEW_COLS = ['Date', 'Time', 'Mobile Number', 'Customer Name', 'Agent Name', 'Product Name', 'Product From', 'Call Status', 'Duration (s)', 'Outcome L1', 'Recording URL'];

const pad = (n: number) => String(n).padStart(2, '0');
const iso = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const monthRange = (offset: number) => {
  const t = new Date();
  const s = new Date(t.getFullYear(), t.getMonth() + offset, 1);
  const e = offset === 0 ? t : new Date(t.getFullYear(), t.getMonth() + offset + 1, 0);
  return { from: iso(s), to: iso(e), label: s.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' }) };
};
const fmtWhen = (s: string | null) =>
  s ? new Date(s).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '';
const num = (n: number) => (n ?? 0).toLocaleString('en-IN');

const qs = (f: Filters) => {
  const p = new URLSearchParams({ from_date: f.from_date, to_date: f.to_date });
  if (f.agent) p.set('agent', f.agent);
  if (f.call_type) p.set('call_type', f.call_type);
  if (f.answered_only) p.set('answered_only', 'true');
  if (f.min_duration) p.set('min_duration', String(f.min_duration));
  return p.toString();
};

const describe = (f: Partial<Filters>) => [
  `${f.from_date} → ${f.to_date}`,
  f.agent || 'all agents',
  f.call_type || null,
  f.answered_only ? 'answered only' : null,
  f.min_duration ? `≥ ${f.min_duration}s` : null,
].filter(Boolean).join(' · ');

/**
 * Reports -> Call Recordings (super admins). The day-to-day "recordings for <month>
 * with mobile, agent and product" request, self-serve: filter, preview, export CSV.
 * Exports build in the background (a month is ~100k rows) and stay downloadable 7 days.
 */
const CallRecordingsReport: React.FC = () => {
  const { userData } = useUser();
  const isSuperAdmin = (userData as { data?: { role?: { role_id?: number } } } | null)?.data?.role?.role_id === 9401;
  const months = useMemo(() => [monthRange(0), monthRange(-1), monthRange(-2), monthRange(-3)], []);

  const [filters, setFilters] = useState<Filters>({
    from_date: months[1].from, to_date: months[1].to, agent: '', call_type: '', answered_only: false, min_duration: 0,
  });
  const [preview, setPreview] = useState<Preview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [starting, setStarting] = useState(false);
  const [downloading, setDownloading] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const days = Math.round((new Date(filters.to_date).getTime() - new Date(filters.from_date).getTime()) / 86400000) + 1;
  const rangeError = !filters.from_date || !filters.to_date ? 'Pick both dates'
    : days < 1 ? 'From date is after to date'
    : days > MAX_DAYS ? `Pick at most ${MAX_DAYS} days per export` : null;

  const set = <K extends keyof Filters>(k: K, v: Filters[K]) => setFilters((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!isSuperAdmin || rangeError) return;
    let cancelled = false;
    const t = setTimeout(() => {
      setLoadingPreview(true);
      setPreviewError(null);
      api.req({ url: `/reports/call-recordings/preview?${qs(filters)}`, method: api.Method.GET })
        .then((res) => { if (!cancelled) setPreview(((res as { data?: Preview })?.data) || null); })
        .catch((e) => { if (!cancelled) { setPreview(null); setPreviewError((e as Error).message || 'Could not load'); } })
        .finally(() => { if (!cancelled) setLoadingPreview(false); });
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
  }, [filters, isSuperAdmin, rangeError]);

  const loadJobs = useCallback(async () => {
    try {
      const res = await api.req({ url: '/reports/call-recordings/exports', method: api.Method.GET });
      const list = ((res as { data?: Job[] })?.data) || [];
      setJobs(list);
      return list;
    } catch {
      return [];
    }
  }, []);

  // Poll only while something is building.
  useEffect(() => {
    if (!isSuperAdmin) return;
    let stopped = false;
    const tick = async () => {
      const list = await loadJobs();
      if (stopped) return;
      if (list.some((j) => j.status === 'queued' || j.status === 'running')) {
        pollRef.current = setTimeout(tick, 4000);
      }
    };
    tick();
    return () => { stopped = true; if (pollRef.current) clearTimeout(pollRef.current); };
  }, [isSuperAdmin, loadJobs, starting]);

  const startExport = async () => {
    if (rangeError) { toastMessage(rangeError, 'error'); return; }
    setStarting(true);
    try {
      const res = await api.req({ url: '/reports/call-recordings/exports', method: api.Method.POST, data: filters }) as
        { success?: boolean; message?: string; detail?: string } | null;
      if (res && res.success === false) throw new Error(res.message || res.detail || 'Could not start the export');
      toastMessage('Export started — it will appear below when ready (usually under a minute for a month)', 'success');
      await loadJobs();
    } catch (e) {
      toastMessage((e as Error).message || 'Could not start the export', 'error');
    } finally {
      setStarting(false);
    }
  };

  const download = async (job: Job) => {
    setDownloading(job.job_id);
    try {
      const token = Cookies.get('crm_auth_token') || import.meta.env.VITE_BEARER_TOKEN;
      const resp = await fetch(`${API_BASE_URL}/reports/call-recordings/exports/${job.job_id}/download`, {
        headers: { Authorization: `Bearer ${token}` }, credentials: 'include',
      });
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        throw new Error((body as { detail?: string }).detail || `Download failed (${resp.status})`);
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = job.filename;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) {
      toastMessage((e as Error).message || 'Download failed', 'error');
    } finally {
      setDownloading(null);
    }
  };

  if (!isSuperAdmin) {
    return (
      <div className="p-6">
        <div className="text-center py-16 bg-white rounded-lg border border-gray-200">
          <Lock className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">Call recording exports are for super admins.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="bg-brandColor p-2 rounded-lg"><Mic className="h-5 w-5 text-white" /></div>
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Call Recordings</h1>
            <p className="text-sm text-gray-500">Recordings with the customer&apos;s mobile, the agent and the product — export as CSV (opens in Excel).</p>
          </div>
        </div>
      </div>

      <ReportNotes items={[
        { term: 'Mobile Number', def: 'The customer\'s number (last 10 digits), never our dialer number.' },
        { term: 'Product / Product From', def: 'What the call was about at the time: the call\'s own order if it has one, otherwise the customer\'s latest order or cart before the call. "Product From" shows the order number or "Cart".' },
        { term: 'Outcome L1–L3', def: 'The first disposition saved on that customer within 60 minutes after the call — the result of that call, not what the lead shows today.' },
        { term: 'Which calls', def: 'Only calls with a recording. Tata Tele click-to-call rows carry none; the same call arrives as a Smartflo row with the recording.' },
        { term: 'Limits', def: 'Up to 93 days per export. Exports stay downloadable for 7 days.' },
      ]} />

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          {months.map((m) => (
            <button key={m.from} onClick={() => setFilters((f) => ({ ...f, from_date: m.from, to_date: m.to }))}
                    className={`px-3 py-1.5 text-sm rounded-lg border ${filters.from_date === m.from && filters.to_date === m.to
                      ? 'border-brandColor text-brandColor bg-green-50 font-medium' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
              {m.label}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3 items-end">
          <label className="text-xs text-gray-500">From
            <input type="date" value={filters.from_date} onChange={(e) => set('from_date', e.target.value)}
                   className="mt-1 w-full h-9 border border-gray-200 rounded-lg px-2 text-sm text-gray-800" />
          </label>
          <label className="text-xs text-gray-500">To
            <input type="date" value={filters.to_date} onChange={(e) => set('to_date', e.target.value)}
                   className="mt-1 w-full h-9 border border-gray-200 rounded-lg px-2 text-sm text-gray-800" />
          </label>
          <label className="text-xs text-gray-500">Agent
            <select value={filters.agent} onChange={(e) => set('agent', e.target.value)}
                    className="mt-1 w-full h-9 border border-gray-200 rounded-lg px-2 text-sm text-gray-800 bg-white">
              <option value="">All agents</option>
              {(preview?.agents || []).map((a) => <option key={a} value={a}>{a}</option>)}
              {filters.agent && !(preview?.agents || []).includes(filters.agent) && <option value={filters.agent}>{filters.agent}</option>}
            </select>
          </label>
          <label className="text-xs text-gray-500">Call type
            <select value={filters.call_type} onChange={(e) => set('call_type', e.target.value)}
                    className="mt-1 w-full h-9 border border-gray-200 rounded-lg px-2 text-sm text-gray-800 bg-white">
              <option value="">All</option>
              <option value="outbound">Outbound (dialer)</option>
              <option value="inbound">Inbound</option>
              <option value="disposition">Agent app / mobile</option>
            </select>
          </label>
          <label className="text-xs text-gray-500">Min duration (sec)
            <input type="number" min={0} value={filters.min_duration || ''} placeholder="0"
                   onChange={(e) => set('min_duration', Math.max(parseInt(e.target.value || '0', 10) || 0, 0))}
                   className="mt-1 w-full h-9 border border-gray-200 rounded-lg px-2 text-sm text-gray-800" />
          </label>
          <label className="flex items-center gap-2 h-9 text-sm text-gray-700">
            <input type="checkbox" checked={filters.answered_only} onChange={(e) => set('answered_only', e.target.checked)} />
            Answered calls only
          </label>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
          <div className="text-sm text-gray-600">
            {rangeError ? <span className="text-red-600">{rangeError}</span>
              : loadingPreview ? <span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Counting…</span>
              : previewError ? <span className="text-red-600">{previewError}</span>
              : preview ? <><b className="text-gray-900">{num(preview.total)}</b> recordings match</> : null}
          </div>
          <button onClick={startExport} disabled={starting || !!rangeError || !preview?.total}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-brandColor text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50">
            {starting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />}
            Export CSV{preview?.total ? ` (${num(preview.total)} rows)` : ''}
          </button>
        </div>
      </div>

      {/* Exports */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <span className="font-semibold text-sm text-gray-900">Exports (last 7 days)</span>
          <button onClick={loadJobs} className="text-gray-400 hover:text-gray-600" title="Refresh"><RefreshCw className="w-4 h-4" /></button>
        </div>
        {jobs.length === 0 ? (
          <p className="text-sm text-gray-500 p-5 text-center">No exports yet. Choose the filters above and click Export CSV.</p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {jobs.map((j) => (
              <li key={j.job_id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
                <div className="min-w-0">
                  <div className="text-sm text-gray-900 font-medium break-all">{j.filename}</div>
                  <div className="text-xs text-gray-500">{describe(j.filters)} · by {j.created_by || '—'} · {fmtWhen(j.created_at)}</div>
                </div>
                <div className="flex items-center gap-3">
                  {j.status === 'done' && (
                    <>
                      <span className="flex items-center gap-1 text-xs text-green-700"><CheckCircle2 className="w-3.5 h-3.5" /> {num(j.rows || 0)} rows</span>
                      <button onClick={() => download(j)} disabled={downloading === j.job_id}
                              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                        {downloading === j.job_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Download
                      </button>
                    </>
                  )}
                  {(j.status === 'queued' || j.status === 'running') && (
                    <span className="flex items-center gap-1.5 text-xs text-gray-500"><Loader2 className="w-3.5 h-3.5 animate-spin" /> {j.status === 'queued' ? 'Waiting…' : 'Building…'}</span>
                  )}
                  {j.status === 'failed' && (
                    <span className="flex items-center gap-1.5 text-xs text-red-600" title={j.error || ''}><AlertTriangle className="w-3.5 h-3.5" /> Failed — try again</span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Preview */}
      {preview && preview.rows.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="px-4 py-3 border-b font-semibold text-sm text-gray-900">Preview — first {preview.rows.length} of {num(preview.total)}</div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 text-gray-500">
                <tr>{PREVIEW_COLS.map((c) => <th key={c} className="text-left px-3 py-2 font-medium whitespace-nowrap">{c}</th>)}</tr>
              </thead>
              <tbody>
                {preview.rows.map((r, i) => (
                  <tr key={i} className="border-t border-gray-100">
                    {PREVIEW_COLS.map((c) => (
                      <td key={c} className="px-3 py-2 text-gray-700 whitespace-nowrap max-w-[260px] truncate">
                        {c === 'Recording URL' && r[c]
                          ? <a href={r[c]} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">Listen</a>
                          : (r[c] || <span className="text-gray-300">—</span>)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default CallRecordingsReport;
